
import { supabase } from '../lib/supabase';

// Persist essay score to Supabase for the logged-in IELTS academy student.
// window.__academyStudentId is set by App.js on login and cleared on logout.
async function saveEssayScore({ band, ta, cc, lr, gra, taskType = 'task2' }) {
  const studentId = window.__academyStudentId;
  if (!studentId) return;
  try {
    await supabase.from('essay_scores').insert({
      student_id:   studentId,
      band_score:   parseFloat(band) || null,
      ta_score:     parseFloat(ta)   || null,
      cc_score:     parseFloat(cc)   || null,
      lr_score:     parseFloat(lr)   || null,
      gra_score:    parseFloat(gra)  || null,
      submitted_at: new Date().toISOString(),
      task_type:    taskType,
    });
  } catch { /* non-fatal — progress tracker will simply show fewer entries */ }
}

// Round to nearest 0.5 band, capped at 9.0
function roundBand(n) {
  return Math.min(9.0, Math.round(parseFloat(n) * 2) / 2);
}

// Add +0.5 calibration to all displayed scores (cap 9.0, round to nearest 0.5)
function calibrateScores(result) {
  function cal(v) {
    return String(Math.min(9.0, Math.round((parseFloat(v || 5) + 0.5) * 2) / 2));
  }
  const out = {
    ...result,
    currentBand: cal(result.currentBand || result.band),
    TA:  { ...(result.TA  || {}), score: cal(result.TA?.score)  },
    CC:  { ...(result.CC  || {}), score: cal(result.CC?.score)  },
    LR:  { ...(result.LR  || {}), score: cal(result.LR?.score)  },
    GRA: { ...(result.GRA || {}), score: cal(result.GRA?.score) },
  };
  out.potentialBand = String(Math.min(9.0, parseFloat(out.currentBand) + 0.5));
  out.band = out.currentBand;
  return out;
}

// Apply +1.5 trial boost to all scores (cap 9.0)
function applyTrialBoost(result) {
  function boost(score) {
    const n = roundBand(score) + 1.5;
    return Math.min(9.0, roundBand(n)).toFixed(1);
  }
  return {
    ...result,
    currentBand:   boost(result.currentBand   || result.band || 5.0),
    potentialBand: boost(result.potentialBand  || (parseFloat(result.band || 5.0) + 0.5)),
    TA:  { ...(result.TA  || {}), score: boost(result.TA?.score  || 5.0) },
    CC:  { ...(result.CC  || {}), score: boost(result.CC?.score  || 5.0) },
    LR:  { ...(result.LR  || {}), score: boost(result.LR?.score  || 5.0) },
    GRA: { ...(result.GRA || {}), score: boost(result.GRA?.score || 5.0) },
    trialBoost: true,
  };
}

// ── Guided paragraph-level feedback ──────────────────────────────────────────
const SYSTEM_PROMPT = `You are a senior IELTS examiner. Score this paragraph honestly against real IELTS Band descriptors.

Scoring rules:
- currentBand: the honest score for what was written
- potentialBand: exactly 0.5 above currentBand
- For each improvement: quote EXACT words in 'original', give EXACT replacement in 'improved' — student must copy-paste directly
- 'what': one short line describing the change
- 'why': one sentence on band score impact
- 'rule': 2-3 sentences on the grammar/vocabulary rule
- rewrite: rewrite the weakest sentence at Band 7.5
- Never score below 5.0 for a genuine attempt. Provide 3-5 improvements.
- "original_with_errors": split the FULL student paragraph text at every error boundary — cover every character with no gaps. Each item: { "text": "exact text", "hasError": false } or { "text": "exact text", "hasError": true, "errorType": "grammar|vocabulary|phrasing|punctuation|structure|cohesion", "explanation": "Friendly encouraging tip max 15 words" }
- "improved_version": the paragraph improved, split to match original_with_errors order and boundaries. Each item: { "text": "...", "isCorrected": false } or { "text": "...", "isCorrected": true, "explanation": "Why better — max 15 words, warm and encouraging", "original": "original text" }

For each criterion (TA, CC, LR, GRA) include these additional fields:
- "why_score": 2-3 sentences explaining EXACTLY why this band was awarded, referencing the student's actual words or sentence structures
- "strengths": array of 1-2 specific things done well — each with a direct quote from their writing inside double-quotes
- "weaknesses": array of 1-2 specific things that limited the score — each with a direct quote showing the exact problem
- "how_to_improve": array of 2-3 actionable IELTS-specific suggestions with stronger alternatives or examples where possible

Also return "repeated_vocabulary": array (max 6) of content words repeated 3+ times (ignore articles, prepositions, conjunctions):
{ "word": "the repeated word", "count": N, "alternatives": ["option1", "option2", "option3", "option4"] }

Return ONLY valid JSON. No markdown, no explanation outside the JSON.`;

const buildUserMessage = ({ promptText, essayTypeLabel, paraLabel, rawText }) => `IELTS Essay Type: ${essayTypeLabel}
Question: "${promptText}"
Paragraph: ${paraLabel}

Student wrote:
"${rawText}"

Return ONLY valid JSON in this exact structure:
{
  "currentBand": "6.0",
  "potentialBand": "6.5",
  "TA": {
    "score": "6.5",
    "comment": "specific comment about their exact words",
    "why_score": "2-3 sentences explaining why this exact score, referencing their actual words",
    "strengths": ["specific strength with quote from their writing in double-quotes"],
    "weaknesses": ["specific limitation with quote showing the problem in double-quotes"],
    "how_to_improve": ["actionable suggestion with example", "suggestion 2", "suggestion 3"]
  },
  "CC": {
    "score": "6.0",
    "comment": "specific comment",
    "why_score": "...",
    "strengths": ["..."],
    "weaknesses": ["..."],
    "how_to_improve": ["...", "...", "..."]
  },
  "LR": {
    "score": "6.0",
    "comment": "specific comment",
    "why_score": "...",
    "strengths": ["..."],
    "weaknesses": ["..."],
    "how_to_improve": ["...", "...", "..."]
  },
  "GRA": {
    "score": "6.5",
    "comment": "specific comment",
    "why_score": "...",
    "strengths": ["..."],
    "weaknesses": ["..."],
    "how_to_improve": ["...", "...", "..."]
  },
  "improvements": [
    {
      "original": "exact words the student wrote that need changing",
      "improved": "exact replacement — word for word what they should write instead",
      "what": "one line: what to change",
      "why": "one sentence: why this improves the band score",
      "rule": "the grammar or vocabulary rule — 2-3 sentences"
    }
  ],
  "rewrite": "the full improved version of their weakest paragraph at Band 7.5",
  "original_with_errors": [
    { "text": "error-free text", "hasError": false },
    { "text": "weak word or phrase", "hasError": true, "errorType": "vocabulary", "explanation": "Try a more academic word here" }
  ],
  "improved_version": [
    { "text": "unchanged text", "isCorrected": false },
    { "text": "stronger word or phrase", "isCorrected": true, "explanation": "More academic, raises your LR band", "original": "weak word or phrase" }
  ],
  "topThreeTips": [
    "Most important thing to practise — specific and actionable",
    "Second improvement — specific",
    "Third improvement — specific"
  ],
  "repeated_vocabulary": [
    { "word": "repeated word", "count": 4, "alternatives": ["option1", "option2", "option3", "option4"] }
  ]
}`;

export async function getFeedback({ promptText, essayTypeLabel, paraLabel, sentences, writings, isTrialUser = false }) {
  const rawText = sentences.map((s, i) => writings[i] || '(not written)').join(' ');

  const response = await fetch('/api/anthropic', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-5',
      max_tokens: 4500,
      system: SYSTEM_PROMPT,
      messages: [{ role: 'user', content: buildUserMessage({ promptText, essayTypeLabel, paraLabel, rawText }) }],
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `API error ${response.status}. Check your API key.`);
  }

  const data = await response.json();
  const text = data.content?.[0]?.text || '{}';

  let result;
  try {
    result = JSON.parse(text.replace(/```json|```/g, '').trim());
  } catch {
    throw new Error('Could not parse feedback. Please try again.');
  }

  result = calibrateScores(result);

  if (isTrialUser) {
    result = applyTrialBoost(result);
  } else {
    saveEssayScore({ band: result.band, ta: result.TA?.score, cc: result.CC?.score, lr: result.LR?.score, gra: result.GRA?.score });
  }

  return result;
}

// ── Full-essay feedback ───────────────────────────────────────────────────────
const SYSTEM_PROMPT_FULL = `You are a senior IELTS examiner. Score this full essay honestly against real IELTS Band descriptors.

Scoring rules:
- currentBand: honest overall band (average of TA+CC+LR+GRA, rounded to nearest 0.5)
- potentialBand: exactly 0.5 above currentBand
- For each improvement: quote EXACT words in 'original', give EXACT replacement in 'improved' — student must copy-paste directly
- 'what': one short line; 'why': one sentence on band impact; 'rule': 2-3 sentences on the rule
- rewrite: rewrite the weakest paragraph at Band 7.5
- Never score below 5.0 for a genuine attempt. Provide 4-6 improvements.

For each criterion (TA, CC, LR, GRA) include these additional fields:
- "why_score": 2-3 sentences explaining EXACTLY why this band was awarded, referencing the student's actual words or sentence structures
- "strengths": array of 1-2 specific things done well — each with a direct quote from their essay in double-quotes
- "weaknesses": array of 1-2 specific things that limited the score — each with a direct quote showing the exact problem
- "how_to_improve": array of 2-3 actionable IELTS-specific suggestions with stronger alternatives or examples

Also return "repeated_vocabulary": array (max 6) of content words repeated 3+ times:
{ "word": "the repeated word", "count": N, "alternatives": ["option1", "option2", "option3", "option4"] }

Return ONLY valid JSON. No markdown.`;

export async function getFullEssayFeedback({ promptText, essay, isTrialUser = false }) {
  const response = await fetch('/api/anthropic', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-5',
      max_tokens: 5000,
      system: SYSTEM_PROMPT_FULL,
      messages: [{
        role: 'user',
        content: `Question: "${promptText}"

Essay:
${essay}

Return ONLY valid JSON:
{
  "currentBand": "6.0",
  "potentialBand": "6.5",
  "TA": {
    "score": "6.5", "comment": "...",
    "why_score": "2-3 sentences referencing their actual words",
    "strengths": ["specific strength with essay quote in double-quotes"],
    "weaknesses": ["specific limitation with essay quote in double-quotes"],
    "how_to_improve": ["suggestion with example", "suggestion 2", "suggestion 3"]
  },
  "CC": { "score": "6.0", "comment": "...", "why_score": "...", "strengths": ["..."], "weaknesses": ["..."], "how_to_improve": ["...", "...", "..."] },
  "LR": { "score": "6.0", "comment": "...", "why_score": "...", "strengths": ["..."], "weaknesses": ["..."], "how_to_improve": ["...", "...", "..."] },
  "GRA": { "score": "6.5", "comment": "...", "why_score": "...", "strengths": ["..."], "weaknesses": ["..."], "how_to_improve": ["...", "...", "..."] },
  "improvements": [
    { "original": "exact student text", "improved": "exact replacement", "what": "one line", "why": "one sentence", "rule": "2-3 sentences" }
  ],
  "rewrite": "improved paragraph at Band 7.5",
  "topThreeTips": ["tip 1", "tip 2", "tip 3"],
  "repeated_vocabulary": [
    { "word": "word", "count": 4, "alternatives": ["option1", "option2", "option3", "option4"] }
  ]
}`,
      }],
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `API error ${response.status}.`);
  }

  const data = await response.json();
  const text = data.content?.[0]?.text || '{}';

  let result;
  try {
    result = JSON.parse(text.replace(/```json|```/g, '').trim());
  } catch {
    throw new Error('Could not parse feedback. Please try again.');
  }

  result = calibrateScores(result);

  if (isTrialUser) {
    result = applyTrialBoost(result);
  } else {
    saveEssayScore({ band: result.band, ta: result.TA?.score, cc: result.CC?.score, lr: result.LR?.score, gra: result.GRA?.score });
  }

  return result;
}

// ── Test Mode: error-annotation side-by-side feedback ────────────────────────
const TEST_MODE_SYSTEM = `You are an expert IELTS examiner. Analyze this essay and return ONLY valid JSON — no markdown, no preamble, no text outside the JSON object.

Return exactly this structure:
{
  "band_score": <overall band 1.0-9.0 nearest 0.5>,
  "TA": {
    "score": <Task Achievement 1.0-9.0>,
    "why_score": "2-3 sentences explaining EXACTLY why this score, referencing the student's actual words",
    "strengths": ["specific strength with direct essay quote in double-quotes"],
    "weaknesses": ["specific limitation with direct essay quote showing the problem"],
    "how_to_improve": ["actionable IELTS suggestion with example", "suggestion 2", "suggestion 3"]
  },
  "CC": { "score": <Coherence & Cohesion>, "why_score": "...", "strengths": ["..."], "weaknesses": ["..."], "how_to_improve": ["...", "...", "..."] },
  "LR": { "score": <Lexical Resource>, "why_score": "...", "strengths": ["..."], "weaknesses": ["..."], "how_to_improve": ["...", "...", "..."] },
  "GRA": { "score": <Grammatical Range & Accuracy>, "why_score": "...", "strengths": ["..."], "weaknesses": ["..."], "how_to_improve": ["...", "...", "..."] },
  "original_with_errors": [
    { "text": "error-free text", "hasError": false },
    { "text": "wrong words", "hasError": true, "errorType": "grammar|punctuation|vocabulary|phrasing|structure", "explanation": "IELTS-focused reason max 12 words" }
  ],
  "improved_version": [
    { "text": "unchanged text", "isCorrected": false },
    { "text": "corrected text", "isCorrected": true, "explanation": "why better max 12 words", "original": "original wrong text" }
  ],
  "overall_feedback": "2-3 sentences on overall IELTS band performance for this specific essay",
  "repeated_vocabulary": [
    { "word": "repeated content word", "count": N, "alternatives": ["option1", "option2", "option3", "option4"] }
  ]
}

Rules:
- Split the ENTIRE essay at every error boundary — cover every character, no gaps
- improved_version must have exactly the same number of items as original_with_errors in the same order
- errorType: grammar, punctuation, vocabulary, phrasing, or structure only
- repeated_vocabulary: content words only (not articles/prepositions/conjunctions) repeated 3+ times, max 6 entries
- Honest scores — never below 4.0 for a genuine attempt
- No markdown, no text before or after the JSON`;

export async function getTestModeFeedback({ promptText, essay }) {
  const response = await fetch('/api/anthropic', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-5',
      max_tokens: 5500,
      system: TEST_MODE_SYSTEM,
      messages: [{ role: 'user', content: `Question: "${promptText}"\n\nEssay:\n${essay}` }],
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || `API error ${response.status}.`);
  }

  const data = await response.json();
  const raw = data.content?.[0]?.text || '{}';

  let result;
  try {
    result = JSON.parse(raw.replace(/```json|```/g, '').trim());
  } catch {
    throw new Error('Could not parse feedback. Please try again.');
  }

  // Apply +0.5 calibration — overall band + all 4 criterion scores
  function calNum(n) {
    return Math.min(9.0, Math.round((parseFloat(n || 5) + 0.5) * 2) / 2);
  }
  function calStr(n) { return String(calNum(n)); }

  result.band_score = calNum(result.band_score);
  ['TA', 'CC', 'LR', 'GRA'].forEach(k => {
    if (result[k]) result[k].score = calStr(result[k].score);
  });

  saveEssayScore({ band: result.band_score, ta: result.TA?.score, cc: result.CC?.score, lr: result.LR?.score, gra: result.GRA?.score });

  return result;
}
